import L from "leaflet";
import {
  lineString,
  point,
  polygon,
  buffer,
  bbox,
  bboxPolygon,
  destination,
} from "@turf/turf";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function uid(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function clamp(v, a, b) {
  return Math.max(a, Math.min(b, Number(v) || 0));
}

function readState(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? { ...fallback, ...parsed } : fallback;
  } catch {
    return fallback;
  }
}

function writeState(key, state) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {}
}

function downloadText(filename, text, type = "text/plain") {
  const blob = new Blob([String(text || "")], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function toCsv(rows, fields) {
  const head = fields.join(",");
  const body = rows.map((r) => fields.map((f) => {
    const raw = r?.[f] ?? "";
    const s = String(raw).replaceAll("\"", "\"\"");
    return /[",\n]/.test(s) ? `"${s}"` : s;
  }).join(","));
  return [head, ...body].join("\n");
}

function latLonText(lat, lon) {
  return `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;
}

function makeSectionCard(title, inner) {
  return `
    <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
      <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">${esc(title)}</summary>
      <div style="margin-top:8px;">${inner}</div>
    </details>
  `;
}

export const forestFiresTool = {
  id: "forest-fires",
  name: "Forest Fires",
  init(app) { this._rt = this._rt ?? createForestFiresRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createForestFiresRuntime(app); this._rt.mount(el); },
};

function createForestFiresRuntime(app) {
  const STATE_KEY = "hydrotools:forest-fires:v1";
  const state = readState(STATE_KEY, {
    enabled: false,
    layer: "fires_viirs_snpp_24",
    opacity: 0.85,
  });
  const LAYERS = [
    { id: "fires_viirs_snpp_24", name: "VIIRS SNPP 24h" },
    { id: "fires_viirs_noaa20_24", name: "VIIRS NOAA-20 24h" },
    { id: "fires_viirs_noaa21_24", name: "VIIRS NOAA-21 24h" },
    { id: "fires_viirs_snpp_7", name: "VIIRS SNPP 7d" },
    { id: "fires_terra_7", name: "MODIS Terra 7d" },
  ];
  const WMS_URL = "https://firms.modaps.eosdis.nasa.gov/mapserver/wms/fires/all";
  let containerEl = null;
  let overlay = null;

  function ensureLayer() {
    if (!state.enabled) {
      if (overlay) {
        app.map.instance.removeLayer(overlay);
        overlay = null;
      }
      return;
    }
    if (overlay) app.map.instance.removeLayer(overlay);
    overlay = L.tileLayer.wms(WMS_URL, {
      layers: state.layer,
      format: "image/png",
      transparent: true,
      opacity: clamp(state.opacity, 0.05, 1),
      attribution: "NASA FIRMS",
      version: "1.1.1",
      crossOrigin: true,
    });
    overlay.addTo(app.map.instance);
  }

  function persist() {
    writeState(STATE_KEY, state);
  }

  function render() {
    if (!containerEl) return;
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Forest Fires</div>
        <div class="small" style="margin-top:6px;">NASA FIRMS hotspot overlays for rapid wildfire situational awareness.</div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <input id="ff-enabled" type="checkbox" ${state.enabled ? "checked" : ""} />
          <span class="small">Show FIRMS fire hotspots</span>
        </label>
        <div class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <label class="small" for="ff-layer" style="min-width:84px;">Dataset</label>
          <select id="ff-layer" style="flex:1;">
            ${LAYERS.map((x) => `<option value="${esc(x.id)}" ${x.id === state.layer ? "selected" : ""}>${esc(x.name)}</option>`).join("")}
          </select>
        </div>
        <div class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <label class="small" for="ff-opacity" style="min-width:84px;">Opacity</label>
          <input id="ff-opacity" type="range" min="0.05" max="1" step="0.05" value="${Number(state.opacity).toFixed(2)}" style="flex:1;" />
          <span class="small">${Math.round(Number(state.opacity) * 100)}%</span>
        </div>
      </div>
    `;

    containerEl.querySelector("#ff-enabled")?.addEventListener("change", (e) => {
      state.enabled = !!e.target.checked;
      persist();
      ensureLayer();
      render();
    });
    containerEl.querySelector("#ff-layer")?.addEventListener("change", (e) => {
      state.layer = String(e.target.value || LAYERS[0].id);
      persist();
      ensureLayer();
      render();
    });
    containerEl.querySelector("#ff-opacity")?.addEventListener("input", (e) => {
      state.opacity = clamp(e.target.value, 0.05, 1);
      persist();
      if (overlay) overlay.setOpacity(state.opacity);
      render();
    });
  }

  function mount(el) {
    containerEl = el;
    ensureLayer();
    render();
  }

  function destroy() {
    if (overlay) {
      app.map.instance.removeLayer(overlay);
      overlay = null;
    }
  }

  return { mount, destroy };
}

export const floodPlanModelTool = {
  id: "flood-plan-model",
  name: "Flood Plan Model",
  init(app) { this._rt = this._rt ?? createFloodPlanRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createFloodPlanRuntime(app); this._rt.mount(el); },
};

function createFloodPlanRuntime(app) {
  const STATE_KEY = "hydrotools:flood-plan-model:v1";
  const state = readState(STATE_KEY, {
    riseFt: 3,
    inlandFactorFtPerFtRise: 220,
    drawMode: false,
    scenarios: [],
  });
  let containerEl = null;
  const baseLayer = L.layerGroup();
  const modelLayer = L.layerGroup();
  let clickHandler = null;
  let dblClickHandler = null;
  let draft = [];

  function persist() {
    writeState(STATE_KEY, state);
  }

  function clearDraft() {
    draft = [];
  }

  function scenarioToGeo(s) {
    if (!Array.isArray(s?.coords) || s.coords.length < 3) return null;
    return polygon([[...s.coords, s.coords[0]]]);
  }

  function redraw() {
    baseLayer.clearLayers();
    modelLayer.clearLayers();

    state.scenarios.forEach((s) => {
      const pts = s.coords.map((c) => [c[1], c[0]]);
      const poly = L.polygon(pts, {
        color: "#1d4ed8",
        weight: 1.5,
        fillColor: "#60a5fa",
        fillOpacity: 0.14,
      }).bindTooltip(`${s.name} baseline`);
      poly.addTo(baseLayer);

      const g = scenarioToGeo(s);
      if (!g) return;
      const nm = (Number(s.riseFt) * Number(s.inlandFactorFtPerFtRise)) / 6076.12;
      const buff = buffer(g, Math.max(0.01, nm), { units: "nauticalmiles" });
      const coords = buff?.geometry?.coordinates?.[0] || [];
      if (coords.length < 3) return;
      L.polygon(coords.map((c) => [c[1], c[0]]), {
        color: "#ef4444",
        weight: 1.4,
        fillColor: "#fb7185",
        fillOpacity: clamp((Number(s.riseFt) / 12) * 0.45, 0.08, 0.45),
        dashArray: "6 4",
      }).bindTooltip(`${s.name} modeled inundation (${Number(s.riseFt).toFixed(1)} ft)`)
        .addTo(modelLayer);
    });

    if (draft.length > 0) {
      L.polyline(draft, { color: "#22d3ee", dashArray: "5 5", weight: 2 }).addTo(baseLayer);
    }
  }

  function addScenarioFromDraft() {
    if (draft.length < 3) return;
    const coords = draft.map((ll) => [ll.lng, ll.lat]);
    state.scenarios.unshift({
      id: uid("flood"),
      name: `Scenario ${state.scenarios.length + 1}`,
      createdAt: new Date().toISOString(),
      coords,
      riseFt: clamp(state.riseFt, 0, 20),
      inlandFactorFtPerFtRise: clamp(state.inlandFactorFtPerFtRise, 30, 800),
    });
    clearDraft();
    persist();
    redraw();
    render();
  }

  function setDrawMode(v) {
    state.drawMode = !!v;
    app.map.instance.getContainer().style.cursor = state.drawMode ? "crosshair" : "";
    if (state.drawMode) app.map.instance.doubleClickZoom.disable();
    else app.map.instance.doubleClickZoom.enable();
    persist();
    render();
  }

  function render() {
    if (!containerEl) return;
    const scenariosHtml = state.scenarios.length
      ? state.scenarios.map((s) => `
        <div class="row" style="gap:6px; align-items:center; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;">
          <input class="fpm-name" data-id="${esc(s.id)}" value="${esc(s.name)}" style="flex:1;" />
          <span class="small">${Number(s.riseFt).toFixed(1)} ft</span>
          <button class="btn fpm-zoom" data-id="${esc(s.id)}">Zoom</button>
          <button class="btn fpm-del" data-id="${esc(s.id)}">x</button>
        </div>
      `).join("")
      : `<div class="small">No scenarios yet. Enable draw mode, click 3+ points, then double-click to save.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Flood Plan Model</div>
        <div class="small" style="margin-top:6px;">Bathtub-style planning model. Draw baseline polygons and preview relative inundation buffers.</div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <input id="fpm-draw" type="checkbox" ${state.drawMode ? "checked" : ""} />
          <span class="small">Draw flood scenario polygon</span>
        </label>
        <div class="row" style="margin-top:8px; gap:6px;">
          <label class="small" style="min-width:88px;">Water rise (ft)</label>
          <input id="fpm-rise" type="number" min="0" max="20" step="0.5" value="${Number(state.riseFt)}" style="flex:1;" />
        </div>
        <div class="row" style="margin-top:8px; gap:6px;">
          <label class="small" style="min-width:88px;">Inland factor</label>
          <input id="fpm-factor" type="number" min="30" max="800" step="10" value="${Number(state.inlandFactorFtPerFtRise)}" style="flex:1;" />
          <span class="small">ft / ft rise</span>
        </div>
        <div class="row" style="margin-top:8px; gap:6px;">
          <button class="btn" id="fpm-cancel-draft">Cancel Draft</button>
          <button class="btn" id="fpm-clear-all" ${state.scenarios.length ? "" : "disabled"}>Clear All</button>
        </div>
      </div>
      ${makeSectionCard(`Scenarios (${state.scenarios.length})`, `<div style="display:grid; gap:6px;">${scenariosHtml}</div>`)}
    `;

    containerEl.querySelector("#fpm-draw")?.addEventListener("change", (e) => setDrawMode(!!e.target.checked));
    containerEl.querySelector("#fpm-rise")?.addEventListener("change", (e) => {
      state.riseFt = clamp(e.target.value, 0, 20);
      persist();
      state.scenarios.forEach((s) => { s.riseFt = state.riseFt; });
      redraw();
      render();
    });
    containerEl.querySelector("#fpm-factor")?.addEventListener("change", (e) => {
      state.inlandFactorFtPerFtRise = clamp(e.target.value, 30, 800);
      persist();
      state.scenarios.forEach((s) => { s.inlandFactorFtPerFtRise = state.inlandFactorFtPerFtRise; });
      redraw();
      render();
    });
    containerEl.querySelector("#fpm-cancel-draft")?.addEventListener("click", () => {
      clearDraft();
      redraw();
      render();
    });
    containerEl.querySelector("#fpm-clear-all")?.addEventListener("click", () => {
      state.scenarios = [];
      persist();
      redraw();
      render();
    });

    containerEl.querySelectorAll(".fpm-del").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        state.scenarios = state.scenarios.filter((s) => String(s.id) !== id);
        persist();
        redraw();
        render();
      });
    });
    containerEl.querySelectorAll(".fpm-name").forEach((el) => {
      el.addEventListener("change", () => {
        const id = String(el.getAttribute("data-id") || "");
        const row = state.scenarios.find((s) => String(s.id) === id);
        if (!row) return;
        row.name = String(el.value || row.name);
        persist();
        redraw();
        render();
      });
    });
    containerEl.querySelectorAll(".fpm-zoom").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        const row = state.scenarios.find((s) => String(s.id) === id);
        if (!row) return;
        const b = L.latLngBounds(row.coords.map((c) => [c[1], c[0]]));
        if (b.isValid()) app.map.instance.fitBounds(b.pad(0.2));
      });
    });
  }

  function mount(el) {
    containerEl = el;
    baseLayer.addTo(app.map.instance);
    modelLayer.addTo(app.map.instance);
    clickHandler = (e) => {
      if (!state.drawMode) return;
      draft.push(e.latlng);
      redraw();
    };
    dblClickHandler = (e) => {
      if (!state.drawMode) return;
      L.DomEvent.stop(e);
      addScenarioFromDraft();
    };
    app.map.instance.on("click", clickHandler);
    app.map.instance.on("dblclick", dblClickHandler);
    redraw();
    render();
  }

  function destroy() {
    if (clickHandler) app.map.instance.off("click", clickHandler);
    if (dblClickHandler) app.map.instance.off("dblclick", dblClickHandler);
    clickHandler = null;
    dblClickHandler = null;
    app.map.instance.getContainer().style.cursor = "";
    app.map.instance.doubleClickZoom.enable();
    baseLayer.remove();
    modelLayer.remove();
  }

  return { mount, destroy };
}

export const stormSurgeModelTool = {
  id: "storm-surge-model",
  name: "Storm Surge Model",
  init(app) { this._rt = this._rt ?? createStormSurgeRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createStormSurgeRuntime(app); this._rt.mount(el); },
};

function createStormSurgeRuntime(app) {
  const STATE_KEY = "hydrotools:storm-surge-model:v1";
  const state = readState(STATE_KEY, {
    drawMode: false,
    surgeFt: 6,
    windFromDeg: 220,
    tracks: [],
  });
  let containerEl = null;
  const layer = L.layerGroup();
  let clickHandler = null;
  let dblClickHandler = null;
  let draft = [];

  function persist() {
    writeState(STATE_KEY, state);
  }

  function redraw() {
    layer.clearLayers();

    state.tracks.forEach((t) => {
      const latlngs = t.coords.map((c) => [c[1], c[0]]);
      L.polyline(latlngs, { color: "#0f172a", weight: 2.2 }).bindTooltip(`${t.name} centerline`).addTo(layer);
      if (latlngs.length < 2) return;
      try {
        const l = lineString(t.coords);
        const widthNm = Math.max(0.1, Number(t.surgeFt) * 0.08);
        const buff = buffer(l, widthNm, { units: "nauticalmiles" });
        const ring = buff?.geometry?.coordinates?.[0] || [];
        if (ring.length > 2) {
          L.polygon(ring.map((c) => [c[1], c[0]]), {
            color: "#dc2626",
            weight: 1.6,
            fillColor: "#f97316",
            fillOpacity: clamp(Number(t.surgeFt) / 20, 0.1, 0.5),
            dashArray: "5 4",
          }).bindTooltip(`${t.name} surge envelope (${Number(t.surgeFt).toFixed(1)} ft)`).addTo(layer);
        }
      } catch {}
    });

    if (draft.length) L.polyline(draft, { color: "#22d3ee", dashArray: "5 5", weight: 2 }).addTo(layer);
  }

  function addTrackFromDraft() {
    if (draft.length < 2) return;
    const coords = draft.map((ll) => [ll.lng, ll.lat]);
    state.tracks.unshift({
      id: uid("surge"),
      name: `Surge Track ${state.tracks.length + 1}`,
      coords,
      surgeFt: clamp(state.surgeFt, 0, 25),
      windFromDeg: clamp(state.windFromDeg, 0, 360),
    });
    draft = [];
    persist();
    redraw();
    render();
  }

  function setDrawMode(v) {
    state.drawMode = !!v;
    app.map.instance.getContainer().style.cursor = state.drawMode ? "crosshair" : "";
    if (state.drawMode) app.map.instance.doubleClickZoom.disable();
    else app.map.instance.doubleClickZoom.enable();
    persist();
    render();
  }

  function render() {
    if (!containerEl) return;
    const rows = state.tracks.length
      ? state.tracks.map((t) => `
        <div class="row" style="gap:6px; align-items:center; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;">
          <input class="ssm-name" data-id="${esc(t.id)}" value="${esc(t.name)}" style="flex:1;" />
          <span class="small">${Number(t.surgeFt).toFixed(1)} ft</span>
          <button class="btn ssm-zoom" data-id="${esc(t.id)}">Zoom</button>
          <button class="btn ssm-del" data-id="${esc(t.id)}">x</button>
        </div>
      `).join("")
      : `<div class="small">No storm-surge tracks yet.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Storm Surge Model</div>
        <div class="small" style="margin-top:6px;">Route-aligned surge envelope planner using a configurable surge height.</div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <input id="ssm-draw" type="checkbox" ${state.drawMode ? "checked" : ""} />
          <span class="small">Draw storm track / exposed shoreline</span>
        </label>
        <div class="row" style="margin-top:8px; gap:6px;">
          <label class="small" style="min-width:88px;">Surge (ft)</label>
          <input id="ssm-surge" type="number" min="0" max="25" step="0.5" value="${Number(state.surgeFt)}" style="flex:1;" />
        </div>
        <div class="row" style="margin-top:8px; gap:6px;">
          <label class="small" style="min-width:88px;">Wind from</label>
          <input id="ssm-wind" type="number" min="0" max="360" step="1" value="${Number(state.windFromDeg)}" style="flex:1;" />
          <span class="small">deg</span>
        </div>
      </div>
      ${makeSectionCard(`Surge Tracks (${state.tracks.length})`, `<div style="display:grid; gap:6px;">${rows}</div>`)}
    `;

    containerEl.querySelector("#ssm-draw")?.addEventListener("change", (e) => setDrawMode(!!e.target.checked));
    containerEl.querySelector("#ssm-surge")?.addEventListener("change", (e) => {
      state.surgeFt = clamp(e.target.value, 0, 25);
      state.tracks.forEach((t) => { t.surgeFt = state.surgeFt; });
      persist();
      redraw();
      render();
    });
    containerEl.querySelector("#ssm-wind")?.addEventListener("change", (e) => {
      state.windFromDeg = clamp(e.target.value, 0, 360);
      state.tracks.forEach((t) => { t.windFromDeg = state.windFromDeg; });
      persist();
      redraw();
      render();
    });

    containerEl.querySelectorAll(".ssm-del").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        state.tracks = state.tracks.filter((t) => String(t.id) !== id);
        persist();
        redraw();
        render();
      });
    });
    containerEl.querySelectorAll(".ssm-name").forEach((el) => {
      el.addEventListener("change", () => {
        const id = String(el.getAttribute("data-id") || "");
        const row = state.tracks.find((t) => String(t.id) === id);
        if (!row) return;
        row.name = String(el.value || row.name);
        persist();
        redraw();
      });
    });
    containerEl.querySelectorAll(".ssm-zoom").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        const row = state.tracks.find((t) => String(t.id) === id);
        if (!row) return;
        const b = L.latLngBounds(row.coords.map((c) => [c[1], c[0]]));
        if (b.isValid()) app.map.instance.fitBounds(b.pad(0.2));
      });
    });
  }

  function mount(el) {
    containerEl = el;
    layer.addTo(app.map.instance);
    clickHandler = (e) => {
      if (!state.drawMode) return;
      draft.push(e.latlng);
      redraw();
    };
    dblClickHandler = (e) => {
      if (!state.drawMode) return;
      L.DomEvent.stop(e);
      addTrackFromDraft();
    };
    app.map.instance.on("click", clickHandler);
    app.map.instance.on("dblclick", dblClickHandler);
    redraw();
    render();
  }

  function destroy() {
    if (clickHandler) app.map.instance.off("click", clickHandler);
    if (dblClickHandler) app.map.instance.off("dblclick", dblClickHandler);
    app.map.instance.getContainer().style.cursor = "";
    app.map.instance.doubleClickZoom.enable();
    layer.remove();
  }

  return { mount, destroy };
}

export const openDriftTool = {
  id: "open-drift",
  name: "Open Drift",
  init(app) { this._rt = this._rt ?? createOpenDriftRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createOpenDriftRuntime(app); this._rt.mount(el); },
};

function createOpenDriftRuntime(app) {
  const STATE_KEY = "hydrotools:open-drift:v1";
  const state = readState(STATE_KEY, {
    setReleaseMode: false,
    release: null,
    currentKts: 1.2,
    currentToDeg: 145,
    windKts: 8,
    windToDeg: 170,
    durationHr: 24,
    particles: 18,
    tracks: [],
  });
  let containerEl = null;
  const layer = L.layerGroup();
  let clickHandler = null;

  function persist() {
    writeState(STATE_KEY, state);
  }

  function simulate() {
    if (!state.release) return;
    const start = [Number(state.release.lon), Number(state.release.lat)];
    const duration = clamp(state.durationHr, 1, 120);
    const n = Math.max(1, Math.round(clamp(state.particles, 1, 120)));
    const rows = [];
    for (let i = 0; i < n; i += 1) {
      const coords = [start];
      let p = point(start);
      for (let h = 1; h <= duration; h += 1) {
        const currentNm = Number(state.currentKts) * 1.0;
        const windNm = Number(state.windKts) * 0.03;
        const jitterNm = Math.random() * 0.12;
        const p1 = destination(p, currentNm, Number(state.currentToDeg), { units: "nauticalmiles" });
        const p2 = destination(p1, windNm, Number(state.windToDeg), { units: "nauticalmiles" });
        const p3 = destination(p2, jitterNm, Math.random() * 360, { units: "nauticalmiles" });
        p = p3;
        coords.push(p.geometry.coordinates);
      }
      rows.push({ id: uid("drift"), coords });
    }
    state.tracks = rows;
    persist();
    redraw();
    render();
  }

  function redraw() {
    layer.clearLayers();
    if (state.release) {
      L.circleMarker([state.release.lat, state.release.lon], {
        radius: 6,
        color: "#0f172a",
        weight: 1.4,
        fillColor: "#f97316",
        fillOpacity: 0.95,
      }).bindTooltip("Release point").addTo(layer);
    }
    state.tracks.forEach((t, idx) => {
      const latlngs = t.coords.map((c) => [c[1], c[0]]);
      L.polyline(latlngs, {
        color: idx === 0 ? "#2563eb" : "#60a5fa",
        weight: idx === 0 ? 2.2 : 1.3,
        opacity: idx === 0 ? 0.95 : 0.58,
      }).addTo(layer);
    });
  }

  function render() {
    if (!containerEl) return;
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Open Drift</div>
        <div class="small" style="margin-top:6px;">First-order drift envelope from wind/current vectors (MVP spill or object drift planning).</div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <input id="od-pick" type="checkbox" ${state.setReleaseMode ? "checked" : ""} />
          <span class="small">Pick release point from map</span>
        </label>
        <div class="small" style="margin-top:6px;">Release: ${state.release ? latLonText(state.release.lat, state.release.lon) : "not set"}</div>
        <div class="row" style="margin-top:8px; gap:6px;"><label class="small" style="min-width:100px;">Current (kts)</label><input id="od-current" type="number" min="0" max="8" step="0.1" value="${Number(state.currentKts)}" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><label class="small" style="min-width:100px;">Current to deg</label><input id="od-current-dir" type="number" min="0" max="360" step="1" value="${Number(state.currentToDeg)}" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><label class="small" style="min-width:100px;">Wind (kts)</label><input id="od-wind" type="number" min="0" max="60" step="0.5" value="${Number(state.windKts)}" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><label class="small" style="min-width:100px;">Wind to deg</label><input id="od-wind-dir" type="number" min="0" max="360" step="1" value="${Number(state.windToDeg)}" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><label class="small" style="min-width:100px;">Duration (hr)</label><input id="od-hours" type="number" min="1" max="120" step="1" value="${Number(state.durationHr)}" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><label class="small" style="min-width:100px;">Particles</label><input id="od-particles" type="number" min="1" max="120" step="1" value="${Number(state.particles)}" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;">
          <button class="btn" id="od-run" ${state.release ? "" : "disabled"}>Run Model</button>
          <button class="btn" id="od-clear" ${state.tracks.length ? "" : "disabled"}>Clear</button>
        </div>
      </div>
      ${makeSectionCard("Output", `<div class="small">${state.tracks.length ? `${state.tracks.length} particle tracks computed.` : "No output yet."}</div>`)}
    `;

    containerEl.querySelector("#od-pick")?.addEventListener("change", (e) => {
      state.setReleaseMode = !!e.target.checked;
      app.map.instance.getContainer().style.cursor = state.setReleaseMode ? "crosshair" : "";
      persist();
      render();
    });
    [
      ["#od-current", "currentKts", 0, 8],
      ["#od-current-dir", "currentToDeg", 0, 360],
      ["#od-wind", "windKts", 0, 60],
      ["#od-wind-dir", "windToDeg", 0, 360],
      ["#od-hours", "durationHr", 1, 120],
      ["#od-particles", "particles", 1, 120],
    ].forEach(([sel, key, mn, mx]) => {
      containerEl.querySelector(sel)?.addEventListener("change", (e) => {
        state[key] = clamp(e.target.value, mn, mx);
        persist();
      });
    });
    containerEl.querySelector("#od-run")?.addEventListener("click", simulate);
    containerEl.querySelector("#od-clear")?.addEventListener("click", () => {
      state.tracks = [];
      persist();
      redraw();
      render();
    });
  }

  function mount(el) {
    containerEl = el;
    layer.addTo(app.map.instance);
    clickHandler = (e) => {
      if (!state.setReleaseMode) return;
      state.release = { lat: e.latlng.lat, lon: e.latlng.lng };
      state.setReleaseMode = false;
      app.map.instance.getContainer().style.cursor = "";
      persist();
      redraw();
      render();
    };
    app.map.instance.on("click", clickHandler);
    redraw();
    render();
  }

  function destroy() {
    if (clickHandler) app.map.instance.off("click", clickHandler);
    clickHandler = null;
    app.map.instance.getContainer().style.cursor = "";
    layer.remove();
  }

  return { mount, destroy };
}

function createPointLogRuntime(app, cfg) {
  const state = readState(cfg.stateKey, {
    captureMode: false,
    pendingLat: null,
    pendingLon: null,
    rows: [],
    form: { ...cfg.defaultForm },
  });
  let containerEl = null;
  const layer = L.layerGroup();
  let clickHandler = null;

  function persist() {
    writeState(cfg.stateKey, state);
  }

  function redraw() {
    layer.clearLayers();
    state.rows.forEach((r, idx) => {
      if (!Number.isFinite(Number(r.lat)) || !Number.isFinite(Number(r.lon))) return;
      L.circleMarker([r.lat, r.lon], {
        radius: 5.5,
        color: "#0f172a",
        weight: 1.2,
        fillColor: cfg.markerColor,
        fillOpacity: 0.9,
      }).bindTooltip(`${idx + 1}. ${r.title || cfg.name}`).addTo(layer);
    });
  }

  function buildRowFromForm() {
    const row = {
      id: uid(cfg.idPrefix),
      createdAt: new Date().toISOString(),
      lat: Number.isFinite(Number(state.pendingLat)) ? Number(state.pendingLat) : null,
      lon: Number.isFinite(Number(state.pendingLon)) ? Number(state.pendingLon) : null,
      ...state.form,
      title: cfg.titleFromForm(state.form),
    };
    state.rows.unshift(row);
    state.form = { ...cfg.defaultForm };
    state.pendingLat = null;
    state.pendingLon = null;
    persist();
    redraw();
    render();
  }

  function exportCsv() {
    if (!state.rows.length) return;
    downloadText(`${cfg.filePrefix}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(state.rows, cfg.csvFields), "text/csv;charset=utf-8");
  }

  function render() {
    if (!containerEl) return;
    const formHtml = cfg.renderForm(state.form, state.pendingLat, state.pendingLon);
    const rowsHtml = state.rows.length
      ? state.rows.map((r, idx) => `
        <div style="border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;">
          <div class="small"><strong>${idx + 1}. ${esc(r.title || cfg.name)}</strong></div>
          <div class="small">${esc(cfg.subtitleFromRow(r))}</div>
          ${Number.isFinite(Number(r.lat)) ? `<div class="small">${esc(latLonText(r.lat, r.lon))}</div>` : ""}
        </div>
      `).join("")
      : `<div class="small">No records yet.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">${esc(cfg.name)}</div>
        <div class="small" style="margin-top:6px;">${esc(cfg.description)}</div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <input id="${cfg.idPrefix}-capture" type="checkbox" ${state.captureMode ? "checked" : ""} />
          <span class="small">Pick map point</span>
        </label>
        ${formHtml}
        <div class="row" style="margin-top:8px; gap:6px;">
          <button id="${cfg.idPrefix}-add" class="btn">Add Record</button>
          <button id="${cfg.idPrefix}-export" class="btn" ${state.rows.length ? "" : "disabled"}>Export CSV</button>
          <button id="${cfg.idPrefix}-clear" class="btn" ${state.rows.length ? "" : "disabled"}>Clear</button>
        </div>
      </div>
      ${makeSectionCard(`Records (${state.rows.length})`, `<div style="display:grid; gap:6px; max-height:260px; overflow:auto;">${rowsHtml}</div>`)}
    `;

    containerEl.querySelector(`#${cfg.idPrefix}-capture`)?.addEventListener("change", (e) => {
      state.captureMode = !!e.target.checked;
      app.map.instance.getContainer().style.cursor = state.captureMode ? "crosshair" : "";
      persist();
      render();
    });
    cfg.bindForm(containerEl, state.form, (next) => {
      state.form = { ...state.form, ...next };
      persist();
    });
    containerEl.querySelector(`#${cfg.idPrefix}-add`)?.addEventListener("click", buildRowFromForm);
    containerEl.querySelector(`#${cfg.idPrefix}-export`)?.addEventListener("click", exportCsv);
    containerEl.querySelector(`#${cfg.idPrefix}-clear`)?.addEventListener("click", () => {
      state.rows = [];
      persist();
      redraw();
      render();
    });
  }

  function mount(el) {
    containerEl = el;
    layer.addTo(app.map.instance);
    clickHandler = (e) => {
      if (!state.captureMode) return;
      state.pendingLat = Number(e.latlng.lat);
      state.pendingLon = Number(e.latlng.lng);
      state.captureMode = false;
      app.map.instance.getContainer().style.cursor = "";
      persist();
      render();
    };
    app.map.instance.on("click", clickHandler);
    redraw();
    render();
  }

  function destroy() {
    if (clickHandler) app.map.instance.off("click", clickHandler);
    clickHandler = null;
    app.map.instance.getContainer().style.cursor = "";
    layer.remove();
  }

  return { mount, destroy };
}

export const illegalFishingReportsTool = {
  id: "illegal-fishing-reports",
  name: "Illegal Fishing Reports",
  init(app) { this._rt = this._rt ?? createIllegalFishingRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createIllegalFishingRuntime(app); this._rt.mount(el); },
};

function createIllegalFishingRuntime(app) {
  return createPointLogRuntime(app, {
    stateKey: "hydrotools:illegal-fishing:v1",
    idPrefix: "ifr",
    filePrefix: "illegal-fishing-incidents",
    name: "Illegal Fishing Reports",
    description: "Capture geolocated observations and incident details for enforcement follow-up.",
    markerColor: "#ef4444",
    defaultForm: { vessel: "", gear: "", severity: "medium", notes: "" },
    titleFromForm: (f) => f.vessel || "Unidentified vessel",
    subtitleFromRow: (r) => `${r.severity || "unknown"} | ${r.gear || "gear n/a"}`,
    csvFields: ["createdAt", "title", "vessel", "gear", "severity", "notes", "lat", "lon"],
    renderForm: (f, lat, lon) => `
      <div class="row" style="margin-top:8px; gap:6px;"><input id="ifr-vessel" value="${esc(f.vessel)}" placeholder="Vessel name or MMSI" style="flex:1;" /></div>
      <div class="row" style="margin-top:8px; gap:6px;"><input id="ifr-gear" value="${esc(f.gear)}" placeholder="Observed gear/activity" style="flex:1;" /></div>
      <div class="row" style="margin-top:8px; gap:6px;"><select id="ifr-severity" style="flex:1;"><option value="low" ${f.severity === "low" ? "selected" : ""}>Low</option><option value="medium" ${f.severity === "medium" ? "selected" : ""}>Medium</option><option value="high" ${f.severity === "high" ? "selected" : ""}>High</option></select></div>
      <div class="row" style="margin-top:8px; gap:6px;"><textarea id="ifr-notes" rows="3" style="width:100%;" placeholder="Narrative notes">${esc(f.notes)}</textarea></div>
      <div class="small" style="margin-top:6px;">Point: ${Number.isFinite(Number(lat)) ? esc(latLonText(lat, lon)) : "not set"}</div>
    `,
    bindForm: (root, _form, commit) => {
      root.querySelector("#ifr-vessel")?.addEventListener("input", (e) => commit({ vessel: String(e.target.value || "") }));
      root.querySelector("#ifr-gear")?.addEventListener("input", (e) => commit({ gear: String(e.target.value || "") }));
      root.querySelector("#ifr-severity")?.addEventListener("change", (e) => commit({ severity: String(e.target.value || "medium") }));
      root.querySelector("#ifr-notes")?.addEventListener("input", (e) => commit({ notes: String(e.target.value || "") }));
    },
  });
}

export const fieldLogTool = {
  id: "field-log",
  name: "Field Log",
  init(app) { this._rt = this._rt ?? createFieldLogRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createFieldLogRuntime(app); this._rt.mount(el); },
};

function createFieldLogRuntime(app) {
  const STATE_KEY = "hydrotools:field-log:v1";
  const state = readState(STATE_KEY, {
    captureMode: false,
    lat: null,
    lon: null,
    task: "",
    weather: "",
    notes: "",
    rows: [],
  });
  let containerEl = null;
  const layer = L.layerGroup();
  let clickHandler = null;

  function persist() {
    writeState(STATE_KEY, state);
  }

  function redraw() {
    layer.clearLayers();
    state.rows.forEach((r) => {
      if (!Number.isFinite(Number(r.lat))) return;
      L.circleMarker([r.lat, r.lon], { radius: 4.5, color: "#065f46", fillColor: "#34d399", fillOpacity: 0.9, weight: 1.2 }).addTo(layer);
    });
  }

  function addRow() {
    state.rows.unshift({
      id: uid("field"),
      createdAt: new Date().toISOString(),
      task: state.task,
      weather: state.weather,
      notes: state.notes,
      lat: Number.isFinite(Number(state.lat)) ? Number(state.lat) : null,
      lon: Number.isFinite(Number(state.lon)) ? Number(state.lon) : null,
    });
    state.task = "";
    state.weather = "";
    state.notes = "";
    persist();
    redraw();
    render();
  }

  function exportMd() {
    const lines = ["# Field Log", ""];
    state.rows.slice().reverse().forEach((r, idx) => {
      lines.push(`## ${idx + 1}. ${new Date(r.createdAt).toLocaleString()}`);
      lines.push(`- Task: ${r.task || "n/a"}`);
      lines.push(`- Weather: ${r.weather || "n/a"}`);
      if (Number.isFinite(Number(r.lat))) lines.push(`- Position: ${latLonText(r.lat, r.lon)}`);
      lines.push(`- Notes: ${r.notes || "n/a"}`);
      lines.push("");
    });
    downloadText(`field-log-${new Date().toISOString().slice(0, 10)}.md`, lines.join("\n"), "text/markdown;charset=utf-8");
  }

  function render() {
    if (!containerEl) return;
    const rowsHtml = state.rows.length
      ? state.rows.map((r, idx) => `<div style="border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;"><div class="small"><strong>${idx + 1}. ${esc(r.task || "Field entry")}</strong></div><div class="small">${esc(new Date(r.createdAt).toLocaleString())}</div><div class="small">${esc(r.weather || "")}</div><div class="small">${esc(r.notes || "")}</div></div>`).join("")
      : `<div class="small">No entries yet.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Field Log</div>
        <div class="small" style="margin-top:6px;">Operational field note log with optional geotag and markdown export.</div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <input id="fld-pick" type="checkbox" ${state.captureMode ? "checked" : ""} />
          <span class="small">Pick position from map</span>
        </label>
        <div class="small" style="margin-top:6px;">Position: ${Number.isFinite(Number(state.lat)) ? esc(latLonText(state.lat, state.lon)) : "not set"}</div>
        <div class="row" style="margin-top:8px;"><input id="fld-task" value="${esc(state.task)}" placeholder="Task" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><input id="fld-weather" value="${esc(state.weather)}" placeholder="Weather / sea state" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><textarea id="fld-notes" rows="3" style="width:100%;" placeholder="Notes">${esc(state.notes)}</textarea></div>
        <div class="row" style="margin-top:8px; gap:6px;"><button class="btn" id="fld-add">Add</button><button class="btn" id="fld-export" ${state.rows.length ? "" : "disabled"}>Export MD</button><button class="btn" id="fld-clear" ${state.rows.length ? "" : "disabled"}>Clear</button></div>
      </div>
      ${makeSectionCard(`Entries (${state.rows.length})`, `<div style="display:grid; gap:6px; max-height:260px; overflow:auto;">${rowsHtml}</div>`)}
    `;

    containerEl.querySelector("#fld-pick")?.addEventListener("change", (e) => {
      state.captureMode = !!e.target.checked;
      app.map.instance.getContainer().style.cursor = state.captureMode ? "crosshair" : "";
      persist();
      render();
    });
    containerEl.querySelector("#fld-task")?.addEventListener("input", (e) => { state.task = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#fld-weather")?.addEventListener("input", (e) => { state.weather = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#fld-notes")?.addEventListener("input", (e) => { state.notes = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#fld-add")?.addEventListener("click", addRow);
    containerEl.querySelector("#fld-export")?.addEventListener("click", exportMd);
    containerEl.querySelector("#fld-clear")?.addEventListener("click", () => {
      state.rows = [];
      persist();
      redraw();
      render();
    });
  }

  function mount(el) {
    containerEl = el;
    layer.addTo(app.map.instance);
    clickHandler = (e) => {
      if (!state.captureMode) return;
      state.lat = Number(e.latlng.lat);
      state.lon = Number(e.latlng.lng);
      state.captureMode = false;
      app.map.instance.getContainer().style.cursor = "";
      persist();
      redraw();
      render();
    };
    app.map.instance.on("click", clickHandler);
    redraw();
    render();
  }

  function destroy() {
    if (clickHandler) app.map.instance.off("click", clickHandler);
    clickHandler = null;
    app.map.instance.getContainer().style.cursor = "";
    layer.remove();
  }

  return { mount, destroy };
}

export const vesselLogTool = {
  id: "vessel-log",
  name: "Vessel Log",
  init(app) { this._rt = this._rt ?? createVesselLogRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createVesselLogRuntime(app); this._rt.mount(el); },
};

function createVesselLogRuntime(_app) {
  const STATE_KEY = "hydrotools:vessel-log:v1";
  const state = readState(STATE_KEY, {
    vessel: "",
    engineHours: "",
    fuelGal: "",
    maintenance: "",
    rows: [],
  });
  let containerEl = null;

  function persist() {
    writeState(STATE_KEY, state);
  }

  function addRow() {
    state.rows.unshift({
      id: uid("vlog"),
      createdAt: new Date().toISOString(),
      vessel: state.vessel,
      engineHours: Number(state.engineHours) || 0,
      fuelGal: Number(state.fuelGal) || 0,
      maintenance: state.maintenance,
    });
    state.engineHours = "";
    state.fuelGal = "";
    state.maintenance = "";
    persist();
    render();
  }

  function render() {
    if (!containerEl) return;
    const totalFuel = state.rows.reduce((s, r) => s + (Number(r.fuelGal) || 0), 0);
    const totalHours = state.rows.reduce((s, r) => s + (Number(r.engineHours) || 0), 0);
    const rowsHtml = state.rows.length
      ? state.rows.map((r, idx) => `<div style="border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;"><div class="small"><strong>${idx + 1}. ${esc(r.vessel || "Vessel")}</strong></div><div class="small">${esc(new Date(r.createdAt).toLocaleString())}</div><div class="small">Engine: ${Number(r.engineHours).toFixed(1)} hr | Fuel: ${Number(r.fuelGal).toFixed(1)} gal</div><div class="small">${esc(r.maintenance || "")}</div></div>`).join("")
      : `<div class="small">No vessel log entries yet.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Vessel Log</div>
        <div class="small" style="margin-top:6px;">Fueling, runtime, and maintenance ledger.</div>
        <div class="row" style="margin-top:8px;"><input id="vl-vessel" value="${esc(state.vessel)}" placeholder="Vessel name" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><input id="vl-hours" type="number" step="0.1" value="${esc(state.engineHours)}" placeholder="Engine hours" style="flex:1;" /><input id="vl-fuel" type="number" step="0.1" value="${esc(state.fuelGal)}" placeholder="Fuel (gal)" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><input id="vl-maint" value="${esc(state.maintenance)}" placeholder="Maintenance note" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><button class="btn" id="vl-add">Add</button><button class="btn" id="vl-export" ${state.rows.length ? "" : "disabled"}>Export CSV</button><button class="btn" id="vl-clear" ${state.rows.length ? "" : "disabled"}>Clear</button></div>
        <div class="small" style="margin-top:8px;">Totals: ${totalHours.toFixed(1)} hr, ${totalFuel.toFixed(1)} gal</div>
      </div>
      ${makeSectionCard(`Log Entries (${state.rows.length})`, `<div style="display:grid; gap:6px; max-height:260px; overflow:auto;">${rowsHtml}</div>`)}
    `;

    containerEl.querySelector("#vl-vessel")?.addEventListener("input", (e) => { state.vessel = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#vl-hours")?.addEventListener("input", (e) => { state.engineHours = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#vl-fuel")?.addEventListener("input", (e) => { state.fuelGal = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#vl-maint")?.addEventListener("input", (e) => { state.maintenance = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#vl-add")?.addEventListener("click", addRow);
    containerEl.querySelector("#vl-export")?.addEventListener("click", () => {
      downloadText(`vessel-log-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(state.rows, ["createdAt", "vessel", "engineHours", "fuelGal", "maintenance"]), "text/csv;charset=utf-8");
    });
    containerEl.querySelector("#vl-clear")?.addEventListener("click", () => {
      state.rows = [];
      persist();
      render();
    });
  }

  function mount(el) {
    containerEl = el;
    render();
  }

  return { mount, destroy() {} };
}

export const marineMammalLogTool = {
  id: "marine-mammal-log",
  name: "Marine Mammal Log",
  init(app) { this._rt = this._rt ?? createMarineMammalRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createMarineMammalRuntime(app); this._rt.mount(el); },
};

function createMarineMammalRuntime(app) {
  return createPointLogRuntime(app, {
    stateKey: "hydrotools:marine-mammals:v1",
    idPrefix: "mml",
    filePrefix: "marine-mammal-log",
    name: "Marine Mammal Observation Log",
    description: "Capture species sightings with behavior and mitigation notes.",
    markerColor: "#8b5cf6",
    defaultForm: { species: "", count: "1", behavior: "", notes: "" },
    titleFromForm: (f) => f.species || "Unknown species",
    subtitleFromRow: (r) => `${r.count || 0} observed | ${r.behavior || "behavior n/a"}`,
    csvFields: ["createdAt", "title", "species", "count", "behavior", "notes", "lat", "lon"],
    renderForm: (f, lat, lon) => `
      <div class="row" style="margin-top:8px; gap:6px;"><input id="mml-species" value="${esc(f.species)}" placeholder="Species" style="flex:1;" /></div>
      <div class="row" style="margin-top:8px; gap:6px;"><input id="mml-count" type="number" min="1" step="1" value="${esc(f.count)}" placeholder="Count" style="flex:1;" /><input id="mml-behavior" value="${esc(f.behavior)}" placeholder="Behavior" style="flex:1;" /></div>
      <div class="row" style="margin-top:8px; gap:6px;"><textarea id="mml-notes" rows="3" style="width:100%;" placeholder="Mitigation notes">${esc(f.notes)}</textarea></div>
      <div class="small" style="margin-top:6px;">Point: ${Number.isFinite(Number(lat)) ? esc(latLonText(lat, lon)) : "not set"}</div>
    `,
    bindForm: (root, _form, commit) => {
      root.querySelector("#mml-species")?.addEventListener("input", (e) => commit({ species: String(e.target.value || "") }));
      root.querySelector("#mml-count")?.addEventListener("input", (e) => commit({ count: String(e.target.value || "1") }));
      root.querySelector("#mml-behavior")?.addEventListener("input", (e) => commit({ behavior: String(e.target.value || "") }));
      root.querySelector("#mml-notes")?.addEventListener("input", (e) => commit({ notes: String(e.target.value || "") }));
    },
  });
}

export const dataTransferTool = {
  id: "data-transfer",
  name: "Data Transfer",
  init(app) { this._rt = this._rt ?? createDataTransferRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createDataTransferRuntime(app); this._rt.mount(el); },
};

function createDataTransferRuntime(_app) {
  const STATE_KEY = "hydrotools:data-transfer:v1";
  const state = readState(STATE_KEY, {
    sourcePath: "",
    destinationPath: "",
    includeMask: "*.csv;*.geojson",
    rows: [],
  });
  let containerEl = null;

  function persist() {
    writeState(STATE_KEY, state);
  }

  function addJob() {
    state.rows.unshift({
      id: uid("xfer"),
      createdAt: new Date().toISOString(),
      sourcePath: state.sourcePath,
      destinationPath: state.destinationPath,
      includeMask: state.includeMask,
      command: `robocopy "${state.sourcePath}" "${state.destinationPath}" ${state.includeMask.split(";").map((x) => x.trim()).filter(Boolean).join(" ")} /E /R:2 /W:2`,
    });
    persist();
    render();
  }

  function render() {
    if (!containerEl) return;
    const rowsHtml = state.rows.length
      ? state.rows.map((r, idx) => `<div style="border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;"><div class="small"><strong>${idx + 1}. ${esc(new Date(r.createdAt).toLocaleString())}</strong></div><div class="small">${esc(r.sourcePath)} -> ${esc(r.destinationPath)}</div><textarea readonly style="margin-top:6px; width:100%; height:54px; resize:vertical; font-family: ui-monospace,Consolas,monospace;">${esc(r.command)}</textarea></div>`).join("")
      : `<div class="small">No transfer jobs yet.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Data Transfer</div>
        <div class="small" style="margin-top:6px;">Build repeatable transfer jobs from boat laptop to office archive.</div>
        <div class="row" style="margin-top:8px;"><input id="dt-src" value="${esc(state.sourcePath)}" placeholder="Source path" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><input id="dt-dst" value="${esc(state.destinationPath)}" placeholder="Destination path" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><input id="dt-mask" value="${esc(state.includeMask)}" placeholder="Masks, semicolon-separated" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><button class="btn" id="dt-add">Add Job</button><button class="btn" id="dt-export" ${state.rows.length ? "" : "disabled"}>Export CSV</button><button class="btn" id="dt-clear" ${state.rows.length ? "" : "disabled"}>Clear</button></div>
      </div>
      ${makeSectionCard(`Transfer Jobs (${state.rows.length})`, `<div style="display:grid; gap:6px; max-height:260px; overflow:auto;">${rowsHtml}</div>`)}
    `;

    containerEl.querySelector("#dt-src")?.addEventListener("input", (e) => { state.sourcePath = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#dt-dst")?.addEventListener("input", (e) => { state.destinationPath = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#dt-mask")?.addEventListener("input", (e) => { state.includeMask = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#dt-add")?.addEventListener("click", addJob);
    containerEl.querySelector("#dt-export")?.addEventListener("click", () => {
      downloadText(`data-transfer-jobs-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(state.rows, ["createdAt", "sourcePath", "destinationPath", "includeMask", "command"]), "text/csv;charset=utf-8");
    });
    containerEl.querySelector("#dt-clear")?.addEventListener("click", () => {
      state.rows = [];
      persist();
      render();
    });
  }

  function mount(el) {
    containerEl = el;
    render();
  }

  return { mount, destroy() {} };
}

export const localNoticeTool = {
  id: "local-notice-mariners",
  name: "Local Notice to Mariners",
  init(app) { this._rt = this._rt ?? createLnmRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createLnmRuntime(app); this._rt.mount(el); },
};

function createLnmRuntime(app) {
  const STATE_KEY = "hydrotools:lnm:v1";
  const state = readState(STATE_KEY, {
    drawMode: false,
    title: "",
    sponsor: "",
    workWindow: "",
    hazards: "",
    route: [],
  });
  let containerEl = null;
  const layer = L.layerGroup();
  let clickHandler = null;

  function persist() {
    writeState(STATE_KEY, state);
  }

  function redraw() {
    layer.clearLayers();
    if (state.route.length > 1) {
      L.polyline(state.route.map((c) => [c[1], c[0]]), { color: "#0f172a", weight: 2.6 }).addTo(layer);
      L.circleMarker([state.route[0][1], state.route[0][0]], { radius: 5, color: "#15803d", fillColor: "#22c55e", fillOpacity: 0.9 }).addTo(layer);
      const last = state.route[state.route.length - 1];
      L.circleMarker([last[1], last[0]], { radius: 5, color: "#7f1d1d", fillColor: "#ef4444", fillOpacity: 0.9 }).addTo(layer);
    }
  }

  function render() {
    if (!containerEl) return;
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Local Notice to Mariners</div>
        <div class="small" style="margin-top:6px;">Draft an LNM bulletin from project metadata and route geometry.</div>
        <div class="row" style="margin-top:8px;"><input id="lnm-title" value="${esc(state.title)}" placeholder="Project title" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><input id="lnm-sponsor" value="${esc(state.sponsor)}" placeholder="Responsible party" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><input id="lnm-window" value="${esc(state.workWindow)}" placeholder="Work window (dates/times)" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px;"><textarea id="lnm-hazards" rows="3" style="width:100%;" placeholder="Hazards and mitigations">${esc(state.hazards)}</textarea></div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;"><input id="lnm-draw" type="checkbox" ${state.drawMode ? "checked" : ""} /><span class="small">Add route waypoints from map clicks</span></label>
        <div class="row" style="margin-top:8px; gap:6px;"><button class="btn" id="lnm-export" ${state.route.length ? "" : "disabled"}>Export Draft (.md)</button><button class="btn" id="lnm-clear-route" ${state.route.length ? "" : "disabled"}>Clear Route</button></div>
        <div class="small" style="margin-top:8px;">Route points: ${state.route.length}</div>
      </div>
    `;

    containerEl.querySelector("#lnm-title")?.addEventListener("input", (e) => { state.title = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#lnm-sponsor")?.addEventListener("input", (e) => { state.sponsor = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#lnm-window")?.addEventListener("input", (e) => { state.workWindow = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#lnm-hazards")?.addEventListener("input", (e) => { state.hazards = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#lnm-draw")?.addEventListener("change", (e) => {
      state.drawMode = !!e.target.checked;
      app.map.instance.getContainer().style.cursor = state.drawMode ? "crosshair" : "";
      persist();
      render();
    });
    containerEl.querySelector("#lnm-clear-route")?.addEventListener("click", () => {
      state.route = [];
      persist();
      redraw();
      render();
    });
    containerEl.querySelector("#lnm-export")?.addEventListener("click", () => {
      const routeList = state.route.map((c, idx) => `${idx + 1}. ${latLonText(c[1], c[0])}`).join("\n");
      const lines = [
        "# Local Notice to Mariners",
        "",
        `## ${state.title || "Untitled Marine Project"}`,
        `- Issued: ${new Date().toLocaleString()}`,
        `- Sponsor: ${state.sponsor || "n/a"}`,
        `- Work Window: ${state.workWindow || "n/a"}`,
        "",
        "### Hazards / Mitigations",
        state.hazards || "n/a",
        "",
        "### Route / Work Area Coordinates",
        routeList || "No coordinates provided.",
      ];
      downloadText(`lnm-draft-${new Date().toISOString().slice(0, 10)}.md`, lines.join("\n"), "text/markdown;charset=utf-8");
    });
  }

  function mount(el) {
    containerEl = el;
    layer.addTo(app.map.instance);
    clickHandler = (e) => {
      if (!state.drawMode) return;
      state.route.push([e.latlng.lng, e.latlng.lat]);
      persist();
      redraw();
      render();
    };
    app.map.instance.on("click", clickHandler);
    redraw();
    render();
  }

  function destroy() {
    if (clickHandler) app.map.instance.off("click", clickHandler);
    clickHandler = null;
    app.map.instance.getContainer().style.cursor = "";
    layer.remove();
  }

  return { mount, destroy };
}

export const fisheriesInteractionTool = {
  id: "fisheries-interaction-map",
  name: "Fisheries Interaction Map",
  init(app) { this._rt = this._rt ?? createFisheriesRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createFisheriesRuntime(app); this._rt.mount(el); },
};

function createFisheriesRuntime(app) {
  const STATE_KEY = "hydrotools:fisheries-interaction:v1";
  const state = readState(STATE_KEY, {
    drawMode: false,
    activeDate: new Date().toISOString().slice(0, 10),
    nextName: "",
    nextSeasonStart: "01-01",
    nextSeasonEnd: "12-31",
    nextGear: "pot/trap",
    zones: [],
  });
  let containerEl = null;
  const layer = L.layerGroup();
  let clickHandler = null;
  let dblClickHandler = null;
  let draft = [];

  function persist() {
    writeState(STATE_KEY, state);
  }

  function monthDayToOrdinal(mmdd) {
    const m = /^\s*(\d{2})-(\d{2})\s*$/.exec(String(mmdd || ""));
    if (!m) return null;
    return (Number(m[1]) * 100) + Number(m[2]);
  }

  function isZoneActive(z) {
    const dt = new Date(`${state.activeDate}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return true;
    const ordinal = ((dt.getMonth() + 1) * 100) + dt.getDate();
    const s = monthDayToOrdinal(z.seasonStart);
    const e = monthDayToOrdinal(z.seasonEnd);
    if (!s || !e) return true;
    if (s <= e) return ordinal >= s && ordinal <= e;
    return ordinal >= s || ordinal <= e;
  }

  function redraw() {
    layer.clearLayers();
    state.zones.forEach((z) => {
      if (!Array.isArray(z.coords) || z.coords.length < 3) return;
      const active = isZoneActive(z);
      L.polygon(z.coords.map((c) => [c[1], c[0]]), {
        color: active ? "#2563eb" : "#64748b",
        weight: active ? 1.6 : 1.2,
        fillColor: active ? "#60a5fa" : "#94a3b8",
        fillOpacity: active ? 0.22 : 0.08,
        dashArray: active ? "" : "4 4",
      }).bindTooltip(`${z.name} | ${z.gear} | ${z.seasonStart}..${z.seasonEnd}`).addTo(layer);
    });
    if (draft.length) L.polyline(draft, { color: "#22d3ee", dashArray: "5 5", weight: 2 }).addTo(layer);
  }

  function addZoneFromDraft() {
    if (draft.length < 3) return;
    state.zones.unshift({
      id: uid("fish"),
      name: state.nextName || `Zone ${state.zones.length + 1}`,
      seasonStart: state.nextSeasonStart,
      seasonEnd: state.nextSeasonEnd,
      gear: state.nextGear,
      coords: draft.map((ll) => [ll.lng, ll.lat]),
    });
    draft = [];
    persist();
    redraw();
    render();
  }

  function render() {
    if (!containerEl) return;
    const rows = state.zones.length
      ? state.zones.map((z) => `<div class="row" style="gap:6px; align-items:center; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;"><div style="flex:1;"><div class="small"><strong>${esc(z.name)}</strong></div><div class="small">${esc(z.gear)} | ${esc(z.seasonStart)}..${esc(z.seasonEnd)}</div></div><button class="btn fim-zoom" data-id="${esc(z.id)}">Zoom</button><button class="btn fim-del" data-id="${esc(z.id)}">x</button></div>`).join("")
      : `<div class="small">No fisheries zones yet.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Fisheries Interaction Map</div>
        <div class="small" style="margin-top:6px;">Draw seasonal gear zones and filter by active date for conflict awareness.</div>
        <div class="row" style="margin-top:8px;"><input id="fim-name" value="${esc(state.nextName)}" placeholder="Zone name" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><input id="fim-gear" value="${esc(state.nextGear)}" placeholder="Gear type" style="flex:1;" /><input id="fim-date" type="date" value="${esc(state.activeDate)}" style="flex:1;" /></div>
        <div class="row" style="margin-top:8px; gap:6px;"><input id="fim-start" value="${esc(state.nextSeasonStart)}" placeholder="Season start MM-DD" style="flex:1;" /><input id="fim-end" value="${esc(state.nextSeasonEnd)}" placeholder="Season end MM-DD" style="flex:1;" /></div>
        <label class="row" style="margin-top:8px; gap:8px; align-items:center;"><input id="fim-draw" type="checkbox" ${state.drawMode ? "checked" : ""} /><span class="small">Draw zone polygon (double-click to finish)</span></label>
      </div>
      ${makeSectionCard(`Zones (${state.zones.length})`, `<div style="display:grid; gap:6px;">${rows}</div>`)}
    `;

    containerEl.querySelector("#fim-name")?.addEventListener("input", (e) => { state.nextName = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#fim-gear")?.addEventListener("input", (e) => { state.nextGear = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#fim-date")?.addEventListener("change", (e) => { state.activeDate = String(e.target.value || state.activeDate); persist(); redraw(); render(); });
    containerEl.querySelector("#fim-start")?.addEventListener("input", (e) => { state.nextSeasonStart = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#fim-end")?.addEventListener("input", (e) => { state.nextSeasonEnd = String(e.target.value || ""); persist(); });
    containerEl.querySelector("#fim-draw")?.addEventListener("change", (e) => {
      state.drawMode = !!e.target.checked;
      app.map.instance.getContainer().style.cursor = state.drawMode ? "crosshair" : "";
      if (state.drawMode) app.map.instance.doubleClickZoom.disable();
      else app.map.instance.doubleClickZoom.enable();
      persist();
      render();
    });
    containerEl.querySelectorAll(".fim-del").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        state.zones = state.zones.filter((z) => String(z.id) !== id);
        persist();
        redraw();
        render();
      });
    });
    containerEl.querySelectorAll(".fim-zoom").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        const z = state.zones.find((x) => String(x.id) === id);
        if (!z) return;
        const b = L.latLngBounds(z.coords.map((c) => [c[1], c[0]]));
        if (b.isValid()) app.map.instance.fitBounds(b.pad(0.2));
      });
    });
  }

  function mount(el) {
    containerEl = el;
    layer.addTo(app.map.instance);
    clickHandler = (e) => {
      if (!state.drawMode) return;
      draft.push(e.latlng);
      redraw();
    };
    dblClickHandler = (e) => {
      if (!state.drawMode) return;
      L.DomEvent.stop(e);
      addZoneFromDraft();
    };
    app.map.instance.on("click", clickHandler);
    app.map.instance.on("dblclick", dblClickHandler);
    redraw();
    render();
  }

  function destroy() {
    if (clickHandler) app.map.instance.off("click", clickHandler);
    if (dblClickHandler) app.map.instance.off("dblclick", dblClickHandler);
    clickHandler = null;
    dblClickHandler = null;
    app.map.instance.getContainer().style.cursor = "";
    app.map.instance.doubleClickZoom.enable();
    layer.remove();
  }

  return { mount, destroy };
}

export const s57s101Tool = {
  id: "s57-s101-viewer",
  name: "S-57/S-101 Viewer",
  init(app) { this._rt = this._rt ?? createS57Runtime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) { if (!this._rt) this._rt = createS57Runtime(app); this._rt.mount(el); },
};

function createS57Runtime(app) {
  const STATE_KEY = "hydrotools:s57-s101-viewer:v1";
  const state = readState(STATE_KEY, {
    datasets: [],
    selectedId: "",
  });
  let containerEl = null;
  const layer = L.layerGroup();

  function persist() {
    writeState(STATE_KEY, state);
  }

  function geometryStyle(ft) {
    const g = String(ft?.geometry?.type || "").toLowerCase();
    if (g.includes("point")) return { radius: 4.5, color: "#1d4ed8", fillColor: "#60a5fa", fillOpacity: 0.9, weight: 1.2 };
    if (g.includes("line")) return { color: "#0f172a", weight: 1.8, opacity: 0.9 };
    return { color: "#334155", weight: 1.2, fillColor: "#93c5fd", fillOpacity: 0.18 };
  }

  function redraw() {
    layer.clearLayers();
    const ds = state.datasets.find((d) => String(d.id) === String(state.selectedId));
    if (!ds) return;
    try {
      L.geoJSON(ds.geojson, {
        style: (ft) => geometryStyle(ft),
        pointToLayer: (ft, latlng) => L.circleMarker(latlng, geometryStyle(ft)),
        onEachFeature: (ft, lyr) => {
          const props = ft?.properties && typeof ft.properties === "object" ? ft.properties : {};
          const pairs = Object.entries(props).slice(0, 8).map(([k, v]) => `${k}: ${v}`);
          lyr.bindTooltip(pairs.join(" | ") || "Feature");
        },
      }).addTo(layer);
    } catch {}
  }

  async function handleFile(file) {
    if (!file) return;
    const name = String(file.name || "dataset");
    const lower = name.toLowerCase();
    const txt = await file.text();
    let gj = null;
    try {
      if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
        gj = JSON.parse(txt);
      }
    } catch {}
    if (!gj || !gj.type) {
      app.ui.toast("MVP viewer currently supports GeoJSON export of S-57/S-101 data.");
      return;
    }
    const entry = { id: uid("enc"), name, loadedAt: new Date().toISOString(), geojson: gj };
    state.datasets.unshift(entry);
    state.selectedId = entry.id;
    persist();
    redraw();
    render();
  }

  function render() {
    if (!containerEl) return;
    const rows = state.datasets.length
      ? state.datasets.map((d) => `<div class="row" style="gap:6px; align-items:center; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;"><button class="btn s57-select" data-id="${esc(d.id)}" style="flex:1; text-align:left;">${esc(d.name)}</button><button class="btn s57-zoom" data-id="${esc(d.id)}">Zoom</button><button class="btn s57-del" data-id="${esc(d.id)}">x</button></div>`).join("")
      : `<div class="small">No datasets loaded.</div>`;

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">S-57 / S-101 Import + Display</div>
        <div class="small" style="margin-top:6px;">MVP viewer for converted GeoJSON from ENC pipelines.</div>
        <div class="row" style="margin-top:8px; gap:6px;"><input id="s57-file" type="file" accept=".geojson,.json,.000" style="flex:1;" /><button class="btn" id="s57-clear" ${state.datasets.length ? "" : "disabled"}>Clear All</button></div>
        <div class="small" style="margin-top:6px;">Selected: ${esc(state.datasets.find((d) => String(d.id) === String(state.selectedId))?.name || "none")}</div>
      </div>
      ${makeSectionCard(`Datasets (${state.datasets.length})`, `<div style="display:grid; gap:6px;">${rows}</div>`)}
    `;

    containerEl.querySelector("#s57-file")?.addEventListener("change", (e) => {
      const file = e.target.files?.[0];
      handleFile(file);
      e.target.value = "";
    });
    containerEl.querySelector("#s57-clear")?.addEventListener("click", () => {
      state.datasets = [];
      state.selectedId = "";
      persist();
      redraw();
      render();
    });
    containerEl.querySelectorAll(".s57-select").forEach((el) => {
      el.addEventListener("click", () => {
        state.selectedId = String(el.getAttribute("data-id") || "");
        persist();
        redraw();
        render();
      });
    });
    containerEl.querySelectorAll(".s57-del").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        state.datasets = state.datasets.filter((d) => String(d.id) !== id);
        if (String(state.selectedId) === id) state.selectedId = state.datasets[0]?.id || "";
        persist();
        redraw();
        render();
      });
    });
    containerEl.querySelectorAll(".s57-zoom").forEach((el) => {
      el.addEventListener("click", () => {
        const id = String(el.getAttribute("data-id") || "");
        const ds = state.datasets.find((d) => String(d.id) === id);
        if (!ds) return;
        try {
          const b = bbox(ds.geojson);
          const poly = bboxPolygon(b);
          const coords = poly.geometry.coordinates[0].map((c) => [c[1], c[0]]);
          const bounds = L.latLngBounds(coords);
          if (bounds.isValid()) app.map.instance.fitBounds(bounds.pad(0.15));
        } catch {}
      });
    });
  }

  function mount(el) {
    containerEl = el;
    layer.addTo(app.map.instance);
    redraw();
    render();
  }

  function destroy() {
    layer.remove();
  }

  return { mount, destroy };
}
